import {
    request as cereCodOtpGenerat,
    verify as verificaCodOtpGenerat,
    otpInregistrare as inregistreazaCodOtpGenerat,
    otpInscriere as verificaContLaInscriereGenerat,
} from '../../../api/generated/sdk.gen';
import type { OtpInscriereResponse } from '../../../api/generated/types.gen';
import { handleResponse } from '../../../core/api/response-handler';
import { sesiuneDinRaspuns, type SesiuneOtp } from '../utils/otp-sesiune';
import type { DateInregistrareCod } from '../utils/inregistrare-cod';

/**
 * Cele doua rute de autentificare cu cod, existente in backend.
 *
 * 🔴 Numele generate sunt `request` si `verify` — generice pana la periculos intr-un
 * fisier care mai contine si alte apeluri. De-aia se importa cu ALIAS si nu se
 * folosesc niciodata direct.
 *
 * 🔴 SDK-ul generat NU arunca la eroare, il intoarce (`ThrowOnError = false`): un
 * `await` fara `handleResponse(...)` merge mai departe ca si cum ar fi reusit,
 * chiar la 400. A produs deja un bug real (revocarea unui link raporta succes).
 * De-aia fiecare apel de aici trece prin `handleResponse`.
 *
 * ⚠️ Rutele sunt 404 astazi: `OtpController` are `@ConditionalOnProperty(auth.otp.enabled)`,
 * iar `application.properties` il tine pe `false` — si in PROD, si pe DEV. Ecranele
 * se pot verifica doar contra unui stub local.
 */

/**
 * Cere un cod pentru identificatorul introdus (telefon sau email) si intoarce
 * MESAJUL serverului.
 *
 * ⚠️ Raspunsul e UNIFORM in toate cazurile — cont inexistent, ELEV, cont fara
 * numar valid, limita atinsa — deliberat, ca sa nu existe oracol de enumerare a
 * conturilor. Deci apelul nu poate spune daca s-a trimis ceva, si niciun text de
 * pe ecran n-are voie s-o afirme.
 *
 * 🔴 Mesajul se intoarce ca sa fie RANDAT CA ATARE. Ecranele sunt agnostice de
 * canal: singurul loc care spune pe unde iese codul e serverul. In ziua in care
 * codul pleaca pe alt canal, frontendul nu se atinge si nu ramane in urma niciun
 * text care minte.
 */
export async function cereCodOtp(identificator: string): Promise<string> {
    // 🔴 Campul din DTO se numeste `identificator` de la T1 incoace — email SAU
    //    telefon, si codul pleaca pe canalul a ce s-a scris. Pana atunci se numea
    //    `email`, iar comentariul de aici spunea ca „asa ramane": tipurile se
    //    genereaza din OpenAPI tocmai ca sa nu ramana asa ceva pe cuvantul nostru.
    const raspuns = await cereCodOtpGenerat({ body: { identificator } });
    const corp = handleResponse(raspuns) as { message?: unknown };
    return typeof corp?.message === 'string' ? corp.message.trim() : '';
}

/**
 * Verifica un cod si intoarce SESIUNEA — aceleasi tokenuri ca la login.
 *
 * 🔴 S-a schimbat la T1 (SP-01). Inainte ruta intorcea `{ resetToken }` si fluxul se
 * termina pe „alege o parola noua"; acum intoarce corpul lui `/api/auth/login`
 * (`AuthenticationResponse`), iar codul devine o intrare in cont. Resetarea parolei
 * ramane pe fluxul ei.
 *
 * 🔴 Din 2026-10-08: e acceptat ORICE cod al omului, neexpirat si nefolosit — nu doar cel mai
 * recent — iar reusita le consuma pe toate. Esecul e tot 400, dar corpul are `motiv`:
 * `COD_EXPIRAT` / `COD_FOLOSIT` cand codul scris chiar a fost emis pentru contul acela, altfel
 * `COD_INVALID` (cod gresit, prea multe incercari, cont neeligibil — acelasi raspuns, ca sa nu
 * existe oracol). `handleResponse` il pune pe `ApiValidationError.motiv`; textul il alege
 * `mesajEsecVerificare` (`utils/otp-mesaje.ts`), pur si testat.
 *
 * Tipul generat al raspunsului e `{ [key: string]: unknown }` (spec-ul nu-l descrie),
 * iar `AuthenticationResponse` are ambele campuri optionale — deci tipurile nu apara
 * de un raspuns fara tokenuri. Verificarea se face la RULARE, in `sesiuneDinRaspuns`,
 * care e pura si testata.
 */
export async function verificaCodOtp(identificator: string, code: string): Promise<SesiuneOtp> {
    const raspuns = await verificaCodOtpGenerat({ body: { identificator, code } });
    return sesiuneDinRaspuns(handleResponse(raspuns));
}

/**
 * Wizardul de inscriere (p8): cere un cod pentru un profesor NOU, direct din
 * Nume + Prenume + Email + Telefon -- fara parola.
 *
 * ⚠️ Raspunsul e UNIFORM (200), la fel ca `cereCodOtp`: email deja inscris SI
 * verificat -> doar trimite codul (login); email nou -> creeaza contul PROFESOR
 * dezactivat si trimite codul; ELEV/ADMIN pe acel email -> nu face nimic. Toate
 * trei ies IDENTIC de aici, deliberat -- anti-enumerare.
 */
/**
 * Primul pas al ecranului «Înscriere» (21.09): exista un cont pe adresa, si daca da (non-ELEV),
 * pleaca direct codul de intrare.
 *
 * 🔴 SPRE DEOSEBIRE de celelalte trei apeluri de aici, raspunsul NU e uniform — ruta spune,
 * deliberat, daca adresa are cont (decizia owner-ului). Ce urmeaza dupa raspuns decide
 * `pasDupaInscriere` (`utils/inscriere-email.ts`), pur si testat.
 *
 * Erorile ies prin `handleResponse`: 400 cu `errors.email`, 429 peste plafonul per IP
 * (`motiv: LIMITA_IP`), 404 cand `auth.otp.enabled` e oprit. Din 2026-10-08, doua cazuri in care
 * contul EXISTA dar codul n-a plecat, cu `motiv` pe eroare (`ApiValidationError.motiv`):
 * - 429 `LIMITA_COD` + `secundePanaLaCodNou`: contul si-a primit numarul maxim de coduri; cele
 *   deja primite raman valabile, deci ecranul merge TOT pe verificare (`esteLimitaDeCoduri`);
 * - 503 `TRIMITERE_ESUATA`: mesajul n-a putut fi trimis; ecranul ramane pe loc (`eroareInscriere`).
 */
export async function verificaContLaInscriere(email: string): Promise<Partial<OtpInscriereResponse>> {
    const raspuns = await verificaContLaInscriereGenerat({ body: { email } });
    return handleResponse(raspuns) ?? {};
}

export async function inregistreazaCodOtp(date: DateInregistrareCod): Promise<string> {
    const raspuns = await inregistreazaCodOtpGenerat({ body: date });
    const corp = handleResponse(raspuns) as { message?: unknown };
    return typeof corp?.message === 'string' ? corp.message.trim() : '';
}
